import { startUiTranslator } from "./uiTranslator";

export const PAGE_LANGUAGE = "en";

/* export const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "ta", label: "தமிழ்" },
  { code: "hi", label: "हिन्दी" },
  { code: "te", label: "తెలుగు" },
  { code: "ml", label: "മലയാളം" },
  { code: "kn", label: "ಕನ್ನಡ" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "pt", label: "Português" },
  { code: "ar", label: "العربية" },
  { code: "zh-CN", label: "中文" },
  { code: "ja", label: "日本語" },
]; */
export const LANGUAGES: { code: string; label: string; short?: string }[] = [
  { code: "en", label: "English" },
  { code: "zh-TW", label: "繁體中文", short: "TW" },
  { code: "zh-CN", label: "简体中文", short: "CN" },
  { code: "ja", label: "日本語" },
  { code: "ms", label: "Bahasa Melayu" },
  { code: "th", label: "ไทย" },
  { code: "vi", label: "Tiếng Việt" },
  { code: "id", label: "Bahasa Indonesia" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "pt", label: "Português" },
];


// "app.ocesha.ai" -> ".ocesha.ai" (undefined for localhost / IPs)
const getRootDomain = (): string | undefined => {
  const host = window.location.hostname;
  if (host === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(host)) return undefined;
  const parts = host.split(".");
  return parts.length > 2 ? "." + parts.slice(-2).join(".") : "." + host;
};

// The selected language is remembered until the user changes it:
//  - cookie "googtrans" kept for 1 year (shared across subdomains, e.g. app./podcastclip.)
//  - copy in localStorage, in case the browser drops cookies
const LANG_STORAGE_KEY = "ui_language";
const ONE_YEAR = 60 * 60 * 24 * 365;

const readCookieLang = (): string | null => {
  const match = document.cookie.match(/(?:^|;\s*)googtrans=\/[^/]+\/([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
};

export const getCurrentLang = (): string => {
  const fromCookie = readCookieLang();
  if (fromCookie) return fromCookie;
  try {
    const saved = localStorage.getItem(LANG_STORAGE_KEY);
    if (saved) {
      setGoogTransCookie(saved);          // restore the cookie for the next visit
      return saved;
    }
  } catch { /* ignore */ }
  return PAGE_LANGUAGE;
};

export const setGoogTransCookie = (lang: string) => {
  const value = `/${PAGE_LANGUAGE}/${lang}`;
  const root = getRootDomain();
  const keep = `path=/; max-age=${ONE_YEAR}; SameSite=Lax`;
  document.cookie = `googtrans=${value}; ${keep}`;
  if (root) document.cookie = `googtrans=${value}; ${keep}; domain=${root}`;
  try { localStorage.setItem(LANG_STORAGE_KEY, lang); } catch { /* ignore */ }
};

export const clearGoogTransCookie = () => {
  const expired = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
  const root = getRootDomain();
  document.cookie = `googtrans=; path=/; ${expired}`;
  document.cookie = `googtrans=; path=/; domain=${window.location.hostname}; ${expired}`;
  if (root) document.cookie = `googtrans=; path=/; domain=${root}; ${expired}`;
  try { localStorage.removeItem(LANG_STORAGE_KEY); } catch { /* ignore */ }
};

/**
 * Starts the interface translation for the saved language.
 * Google's website translator widget (translate_a/element.js) was retired by Google on
 * Oct 1, 2026; the app now uses its own translator with translations stored in the
 * database (see utils/uiTranslator.ts and backend routers/i18n.py).
 * The name is kept so index.tsx does not need to change.
 */
export const initGoogleTranslate = () => {
  // A language saved earlier that is no longer offered (e.g. Tamil) -> back to English
  const saved = getCurrentLang();
  if (saved !== PAGE_LANGUAGE && !LANGUAGES.some((l) => l.code === saved)) clearGoogTransCookie();
  applyDirection(getCurrentLang());
  protectEditableContent();
  startUiTranslator(getCurrentLang());
};

// Elements Google Translate must never touch (editors, editable content, code)
const NO_TRANSLATE_SELECTOR = [
  "[contenteditable]",
  ".ql-editor",
  ".ql-container",
  ".tox-tinymce",
  ".tox-edit-area",
  ".mce-content-body",
  ".ck-editor",
  ".ck-editor__editable",
  ".fr-box",
  ".fr-element",
  ".jodit-container",
  ".jodit-wysiwyg",
  ".ProseMirror",
  ".DraftEditor-root",
  "pre",
  "code",
].join(",");

const markNoTranslate = (el: HTMLElement) => {
  if (el.getAttribute("translate") !== "no") {
    el.setAttribute("translate", "no");
    el.classList.add("notranslate");
  }
};

const protectTree = (root: HTMLElement | Document) => {
  if (root instanceof HTMLElement && root.matches(NO_TRANSLATE_SELECTOR)) {
    markNoTranslate(root);
  }
  root.querySelectorAll<HTMLElement>(NO_TRANSLATE_SELECTOR).forEach(markNoTranslate);
};

export const protectEditableContent = () => {
  protectTree(document);
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((m) =>
      m.addedNodes.forEach((n) => {
        if (n instanceof HTMLElement) protectTree(n);
      })
    );
  });
  observer.observe(document.body, { childList: true, subtree: true });
};

export const RTL_LANGUAGES = ["ar", "he", "fa", "ur"];

export const isRtlLang = (lang: string) => RTL_LANGUAGES.includes(lang.split("-")[0]);

export const applyDirection = (lang: string) => {
  const rtl = isRtlLang(lang);
  document.documentElement.dir = rtl ? "rtl" : "ltr";
  document.documentElement.setAttribute("data-dir", rtl ? "rtl" : "ltr");
};